import React from 'react';
import { motion } from 'framer-motion';
import { Flame, TrendingUp, Calendar } from 'lucide-react';

const StudyStreakVisualization = ({ studyStreakData }) => {
  if (!studyStreakData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No study streak data available</p>
      </motion.div>
    );
  }

  const { currentStreak, longestStreak, streakPattern, averageStudyDuration, motivationLevel } = studyStreakData;

  const getStreakColor = (streak) => {
    if (streak >= 30) return 'from-orange-500 to-red-500';
    if (streak >= 14) return 'from-yellow-500 to-orange-500';
    if (streak >= 7) return 'from-green-500 to-yellow-500';
    return 'from-blue-500 to-green-500';
  };

  const getMotivationMessage = (level) => {
    if (level >= 80) return '🔥 On fire! Keep it up!';
    if (level >= 60) return '💪 Great momentum!';
    if (level >= 40) return '👍 Good progress!';
    return '🌱 Building consistency...';
  };

  const getPatternBadge = (pattern) => {
    const badges = {
      consistent: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', text: 'Consistent' },
      irregular: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', text: 'Irregular' },
      building: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', text: 'Building' }
    };
    return badges[pattern] || badges.building;
  };

  const patternBadge = getPatternBadge(streakPattern);

  // Generate calendar heatmap data (last 30 days)
  const generateHeatmapData = () => {
    const days = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const isActive = i < currentStreak;
      days.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        active: isActive,
        intensity: isActive ? Math.min(100, (currentStreak - i) * 10) : 0
      });
    }
    return days;
  };

  const heatmapData = generateHeatmapData();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-800 dark:to-gray-700 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 10, -10, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 1
            }}
            className={`p-3 bg-gradient-to-br ${getStreakColor(currentStreak)} rounded-lg`}
          >
            <Flame className="h-6 w-6 text-white" />
          </motion.div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Study Streak</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{getMotivationMessage(motivationLevel)}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${patternBadge.color}`}>
          {patternBadge.text}
        </span>
      </div>

      {/* Streak Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center"
        >
          <motion.p
            key={currentStreak}
            initial={{ scale: 1.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-4xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent"
          >
            {currentStreak}
          </motion.p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Current Streak</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center"
        >
          <p className="text-4xl font-bold text-purple-600">{longestStreak}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Longest Streak</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center"
        >
          <p className="text-4xl font-bold text-blue-600">{averageStudyDuration}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Avg. Minutes/Day</p>
        </motion.div>
      </div>

      {/* Calendar Heatmap */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Last 30 Days
        </h4>
        <div className="grid grid-cols-10 gap-1">
          {heatmapData.map((day, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.02 }}
              className={`aspect-square rounded ${
                day.active
                  ? `bg-gradient-to-br ${getStreakColor(currentStreak)} opacity-${Math.max(20, day.intensity)}`
                  : 'bg-gray-200 dark:bg-gray-600'
              }`}
              title={day.date}
            />
          ))}
        </div>
      </div>

      {/* Motivation Bar */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Motivation Level</span>
          <span className="text-sm font-bold text-gray-900 dark:text-white">{motivationLevel}%</span>
        </div>
        <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${motivationLevel}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className={`h-full bg-gradient-to-r ${getStreakColor(currentStreak)}`}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default StudyStreakVisualization;
